import { useMemo, useState } from 'react'
import { Boxes, FileText, Pencil, Plus, RefreshCw, Trash2, Download, Flag, Inbox, CalendarClock } from 'lucide-react'
import {
  DECISIONS, Page, PageHeader, Button, IconButton, FileDrop, Field, Input, Select, SearchSelect, DateRangePicker, DATE_RANGE_PRESETS, Textarea, SearchInput, bkkTodayISO,
  FilterCard, useFilters, Loading, EmptyState, NoMatchState, ErrorState, InlineError, Banner, Badge, CountBadge, StatTile, Checkbox, Switch, ChoiceChips,
  DataTable, useTableState, Tabs, SubTabs, Segmented, ViewToggle, Drawer, DrawerFooter, Modal, ConfirmDialog,
  ToastProvider, useToast, docFooterLine, PatientLink, PatientName, patientHref, BranchSelect, useBranches, branchFilterIds, branchName, fmtDate, fmtDateTime, fmtDateText, fmtMonth, fmtDocDate, fmtMoney, fmtBytes,
} from './index.js'

/**
 * /kit — the living catalogue of the shared UI kit, for review before any page
 * moves onto it. Every sample here is INVENTED: no patient data, no API calls,
 * nothing is saved.
 */
export default function KitGallery() {
  return (
    <ToastProvider>
      <Page>
        <PageHeader
          icon={Boxes}
          title="UI Kit"
          subtitle="ชิ้นส่วนกลางของ CRM — ทุกหน้าจะใช้ชุดนี้ ข้อมูลในหน้านี้เป็นตัวอย่างทั้งหมด ไม่มีการบันทึกจริง"
          meta={<>โฟลเดอร์ <code className="text-[12.5px] bg-slate-100 px-1 rounded">src/kit/</code> · ปรับตามคำตอบ 18/10/2026</>}
        />
        <Toc />
        <Decisions />
        <PageSection />
        <ButtonSection />
        <FieldSection />
        <FileDropSection />
        <DropdownSection />
        <FilterSection />
        <DateRangeSection />
        <BranchSection />
        <TableSection />
        <TabsSection />
        <ChoiceSection />
        <OverlaySection />
        <ToastSection />
        <StatesSection />
        <BadgeSection />
        <StatSection />
        <FormatSection />
        <LinkSection />
      </Page>
    </ToastProvider>
  )
}

/* ------------------------------------------------------------------ chrome */

const SECTIONS = [
  ['decisions', 'ค่าที่ตัดสินใจแล้ว'], ['page', 'หัวหน้า (PageHeader)'], ['buttons', 'ปุ่ม'], ['fields', 'ช่องกรอก'], ['filedrop', 'อัปโหลดไฟล์'], ['dropdowns', 'Dropdown'],
  ['filters', 'ค้นหา / กรอง'], ['daterange', 'ช่วงวันที่'], ['branches', 'สาขา'], ['table', 'ตาราง'], ['tabs', 'แท็บ & ตัวเลือก'], ['choices', 'Checkbox / Switch / ชิปสี'], ['overlays', 'Drawer / Modal / ยืนยัน'],
  ['toast', 'ข้อความแจ้ง (Toast)'], ['states', 'สถานะ โหลด / ว่าง / ผิดพลาด'], ['badges', 'ป้ายสถานะ'], ['stats', 'ตัวเลขสรุป'], ['format', 'วันที่ & เงิน'], ['links', 'ลิงก์คนไข้'],
]

function Toc() {
  return (
    <nav className="flex flex-wrap gap-1.5 mb-8">
      {SECTIONS.map(([id, label]) => (
        <a key={id} href={`#${id}`} className="px-2.5 py-1 rounded-lg text-[12.5px] text-slate-600 bg-white ring-1 ring-slate-200 hover:ring-brand-300 hover:text-brand-700">{label}</a>
      ))}
    </nav>
  )
}

function DecisionTag({ inline = false }) {
  return <span className={`${inline ? '' : 'ml-2 '}inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-800 ring-1 ring-amber-200 text-[11.5px] font-semibold align-middle`}><Flag size={11} />ตัดสินใจ</span>
}

function Section({ id, title, decision = false, why, rules, children }) {
  return (
    <section id={id} className="scroll-mt-6 mb-12">
      <h2 className="text-[18px] font-semibold text-slate-900 tracking-tight">{title}{decision && <DecisionTag />}</h2>
      {why && <p className="text-[13.5px] text-slate-500 mt-1 max-w-[820px]">{why}</p>}
      {rules && (
        <ul className="mt-2 space-y-0.5 text-[13px] text-slate-600 list-disc pl-5 max-w-[820px]">
          {rules.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  )
}

function Demo({ label, children, className = '' }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-[#fafbfc]">
      {label && <div className="px-4 pt-3 text-[11.5px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>}
      <div className={`p-4 ${className}`}>{children}</div>
    </div>
  )
}

/* -------------------------------------------------------------- sections */

function Decisions() {
  const rows = [
    ['1', 'วันที่', 'dd/mm/yyyy ค.ศ. บนหน้าจอทุกที่ · พ.ศ. เฉพาะเอกสาร/รายงานที่พิมพ์ · ยกเว้นบรรทัด log เล็ก ๆ ที่ footer ของเอกสาร (เช่น "Printed 18/10/2026 10:30 · Treatment ID …") ใช้ ค.ศ. ภาษาอังกฤษได้', 'format.js → fmtDocDate / docFooterLine'],
    ['2', 'สาขาสนามเป้า', 'ไม่มีให้เลือกในฟอร์มและตัวกรอง · กรอง "อารีย์" = อารีย์ + สนามเป้า · ข้อมูลเก่าของสนามเป้าแสดงเป็น "อารีย์"', 'branches.js'],
    ['3', 'แจ้งผลหลังบันทึก', 'Toast เล็กด้านล่าง หายเองใน 3 วินาที', 'Toast.jsx'],
    ['4', 'คลิกแถว', 'เปิด Drawer ด้านขวา หรือเข้าหน้ารายละเอียด (แล้วแต่หน้า) · เปิดคนไข้ได้จากชื่อ นามสกุล HN เท่านั้น', 'PatientName'],
    ['5', 'มือถือ', 'เมนูหลักใช้บนมือถือได้แล้ว: จอแคบกว่า 1024px มีแถบด้านบน ☰ เปิดเมนูจากด้านซ้าย (ยังไม่ deploy)', 'components/Layout.jsx'],
    ['6', 'ลิงก์คนไข้', 'เปิดแท็บการรักษาเป็นหลัก · จากหน้านัดหมาย → แท็บนัดหมาย · จากตาราง Lab → แท็บ Lab', 'decisions.js → patientTabByContext'],
  ]
  return (
    <Section id="decisions" title="ค่าที่ตัดสินใจแล้ว" why="ตัดสินใจโดยคุณหมอราม 18/10/2026 — เปลี่ยนได้ที่ src/kit/decisions.js ที่เดียว">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13.5px] min-w-[720px]">
          <thead><tr className="bg-slate-50/60 border-b border-slate-200 text-[12px] text-slate-500 text-left">
            <th className="px-4 py-2.5 w-10">#</th><th className="px-4 py-2.5">เรื่อง</th><th className="px-4 py-2.5">ข้อตกลง</th><th className="px-4 py-2.5">อยู่ที่</th>
          </tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r[0]} className="border-b border-slate-100 last:border-0 align-top">
              <td className="px-4 py-2.5 text-slate-400">{r[0]}</td>
              <td className="px-4 py-2.5 font-medium text-slate-800 whitespace-nowrap">{r[1]}{r[4] && <DecisionTag />}</td>
              <td className="px-4 py-2.5 text-slate-700">{r[2]}</td><td className="px-4 py-2.5 text-[12.5px] text-slate-500">{r[3]}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </Section>
  )
}

function PageSection() {
  return (
    <Section id="page" title="หัวหน้าเพจ — Page + PageHeader"
      why="ทุกหน้าเริ่มแบบเดียวกัน: ชื่อหน้าด้านซ้าย ปุ่มหลักหนึ่งปุ่มด้านขวาบน คำอธิบายสั้นหนึ่งบรรทัด"
      rules={[
        'ปุ่มหลัก (สีน้ำเงินทึบ) มีได้หนึ่งปุ่มต่อหน้า อยู่ขวาบนเสมอ — ไม่อยู่ในแถวตัวกรองหรือท้ายหน้า',
        'หน้ารายละเอียดมีลิงก์ "← กลับ" ไปหน้ารายการ · หน้าย่อยของศูนย์ (เช่น รายงาน) ใช้ breadcrumb',
        'ความกว้าง 3 แบบ: ปกติ 1200px · กว้าง (ปฏิทิน/ตาราง matrix) · แคบ 820px (หน้าส่วนตัว / ฟอร์ม)',
      ]}>
      <Demo label="หน้ารายการ — ชื่อ + คำอธิบาย + ปุ่มหลัก">
        <PageHeader title="งาน Lab" subtitle="งานแล็บทุกสาขา เรียงตามวันนัดรับ"
          actions={<><Button variant="secondary" icon={Download}>ส่งออก</Button><Button icon={Plus}>เพิ่มงาน Lab</Button></>} />
      </Demo>
      <Demo label="หน้าย่อยของศูนย์ — breadcrumb + ไอคอน">
        <PageHeader icon={FileText} breadcrumb={[{ label: 'รายงาน', to: '/reports' }, { label: 'รายรับ' }]} title="รายงานรายรับ" subtitle="ยอดเก็บจริงต่อวัน แยกสาขาและแพทย์" />
      </Demo>
      <Demo label="หน้ารายละเอียด — ลิงก์กลับ + ปุ่มแก้ไข">
        <PageHeader back={{ to: '/kit', label: 'กลับไปที่ทะเบียนสินทรัพย์' }} title="เครื่องขูดหินปูน #A-0012" meta={<Badge tone="emerald" dot>ใช้งานได้</Badge>}
          actions={<Button variant="soft" icon={Pencil}>แก้ไข</Button>} />
      </Demo>
      <Demo label="หน้าย่อยในเลย์เอาต์ — level 2 (18px)">
        <PageHeader level={2} title="ยอดวันลา" subtitle="โควตาและวันที่ใช้ไปของพนักงานแต่ละคน" actions={<Button icon={Plus}>บันทึกการลา</Button>} />
      </Demo>
    </Section>
  )
}

function ButtonSection() {
  const [busy, setBusy] = useState(false)
  return (
    <Section id="buttons" title="ปุ่ม — Button / IconButton"
      why="เดิมมีปุ่มหลัก 195 ปุ่ม เขียนแยกกัน ขนาดและความโค้งไม่เท่ากัน — ตอนนี้เหลือ 6 แบบ 2 ขนาด"
      rules={[
        'primary = การกระทำหลักของพื้นที่นั้น (หนึ่งปุ่ม) · secondary = ยกเลิก / ทางเลือกรอง · soft = "แก้ไข" บนหน้าดูข้อมูล',
        'danger (แดงทึบ) ใช้ในกล่องยืนยันการลบเท่านั้น · danger-soft = จุดเริ่ม "ลบ" ที่จะถามยืนยันก่อน',
        'ขนาด md สำหรับหัวหน้า/ท้าย Drawer · sm สำหรับในการ์ด ตาราง ตัวกรอง · ปุ่มไอคอนอย่างเดียวต้องมีคำอธิบาย (tooltip)',
      ]}>
      <Demo label="variant — md" className="flex flex-wrap items-center gap-2">
        <Button icon={Plus}>เพิ่มรายการ</Button>
        <Button variant="secondary">ยกเลิก</Button>
        <Button variant="soft" icon={Pencil}>แก้ไข</Button>
        <Button variant="ghost" icon={RefreshCw}>รีเฟรช</Button>
        <Button variant="danger-soft" icon={Trash2}>ลบ</Button>
        <Button variant="danger">ลบถาวร</Button>
      </Demo>
      <Demo label="size sm · loading · disabled · icon only" className="flex flex-wrap items-center gap-2">
        <Button size="sm" icon={Plus}>เพิ่ม</Button>
        <Button size="sm" variant="secondary">ยกเลิก</Button>
        <Button loading={busy} onClick={() => { setBusy(true); setTimeout(() => setBusy(false), 1500) }}>{busy ? 'กำลังบันทึก…' : 'กดเพื่อดู loading'}</Button>
        <Button disabled>ปิดใช้งาน</Button>
        <Button disabled disabledReason="วันนี้ปิดยอดแล้ว — แก้ไขไม่ได้" variant="secondary">ชี้เมาส์ดูเหตุผล</Button>
        <IconButton icon={RefreshCw} label="รีเฟรช" />
        <IconButton icon={Trash2} label="ลบ" variant="danger-soft" />
      </Demo>
    </Section>
  )
}

const BRANCH_OPTIONS = [{ value: 8, label: 'อารีย์' }, { value: 3, label: 'บางนา' }, { value: 5, label: 'ลาดพร้าว' }, { value: 6, label: 'รามอินทรา' }]

function FieldSection() {
  const [q, setQ] = useState('')
  const [branch, setBranch] = useState('')
  return (
    <Section id="fields" title="ช่องกรอก — Field / Input / Select / Textarea / SearchInput"
      why="ป้ายชื่ออยู่เหนือช่องเสมอ ช่องที่จำเป็นมี * สีแดง ข้อความแนะนำสีเทาใต้ช่อง และข้อความผิดพลาดสีแดงแทนที่ข้อความแนะนำ"
      rules={['บอกล่วงหน้าว่าช่องไหนจำเป็น (*) แทนการปล่อยให้กดบันทึกแล้วค่อยรู้', 'ช่องค้นหามีปุ่ม ✕ เสมอเมื่อมีข้อความ · ถ้าค้นเฉพาะหน้านี้ ให้บอกในช่อง']}>
      <Demo className="grid sm:grid-cols-2 gap-4">
        <Field label="ชื่อรายการ" required hint="ตามที่จะแสดงในใบเสร็จ"><Input placeholder="เช่น ขูดหินปูน" /></Field>
        <Field label="สาขา" required><Select value={branch} onChange={setBranch} options={BRANCH_OPTIONS} placeholder="เลือกสาขา…" /></Field>
        <Field label="ราคา (บาท)" error="ต้องมากกว่า 0"><Input inputMode="numeric" defaultValue="0" /></Field>
        <Field label="ปิดใช้งาน" hint="ข้อมูลจากระบบ แก้ไม่ได้"><Input disabled defaultValue="HN 000000" /></Field>
        <Field label="หมายเหตุ" className="sm:col-span-2"><Textarea placeholder="ไม่บังคับ" /></Field>
        <Field label="ค้นหา"><SearchInput value={q} onChange={setQ} /></Field>
        <Field label="ค้นหาเฉพาะหน้านี้"><SearchInput value={q} onChange={setQ} scope="หน้านี้" /></Field>
      </Demo>
    </Section>
  )
}

function FileDropSection() {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const take = (f) => { setBusy(true); setTimeout(() => { setBusy(false); setName(f.name) }, 900) }
  return (
    <Section id="filedrop" title="อัปโหลดไฟล์ — FileDrop"
      why="ลากไฟล์มาวางหรือคลิกเลือก ไฟล์เดียว อ่านในเครื่อง — ใช้กับหน้านำเข้าตามเบิก (ปกส. PDF / สปสช. Excel) ให้หน้าตาเหมือนกันทุกแท็บ"
      rules={['มีไฟล์แล้วจะแสดงชื่อไฟล์ + ปุ่ม ล้าง มุมขวาบน — ล้างทุกอย่างที่ได้จากไฟล์นั้น ไม่ใช่แค่ชื่อ', 'ตรวจนามสกุล/ขนาดไฟล์ในหน้า แล้วแจ้งด้วย Banner สีแดงใต้กล่อง', 'ระหว่างอ่านไฟล์ (busy) วางไฟล์ใหม่หรือกดล้างไม่ได้']}>
      <Demo label="ลองลากไฟล์อะไรก็ได้มาวาง (ไม่มีการอ่านจริง)">
        <FileDrop label="ลากไฟล์ Excel (.xlsx) มาวางที่นี่" kind="sheet" accept=".xlsx"
          fileName={name} busy={busy} onFile={take} onClear={() => setName('')} />
      </Demo>
    </Section>
  )
}

const DOCTORS = [
  ['หมอเอ', 'ทพ. เอกชัย · จัดฟัน · อารีย์'], ['หมอเอ', 'ทพญ. เอมิกา · ทั่วไป · บางนา'], ['หมอบี', 'ทพ. บุญชัย · รากเทียม'],
  ['หมอซี', 'ทพญ. ชลธิชา · เด็ก'], ['หมอดี', 'ทพ. ดนัย · ศัลย์'], ['หมออี', 'ทพญ. อรวรรณ · รักษารากฟัน'],
  ['หมอเอฟ', 'ทพ. ฟ้าใส · ทั่วไป'], ['หมอจี', 'ทพญ. จิราพร · จัดฟัน'], ['หมอเอช', 'ทพ. เฮง · ปริทันต์'],
  ['หมอไอ', 'ทพญ. ไอลดา · ทั่วไป'], ['หมอเจ', 'ทพ. เจษฎา · ประดิษฐ์'], ['หมอเค', 'ทพญ. กมลชนก · เด็ก'],
].map(([label, hint], i) => ({ value: 100 + i, label, hint, group: i < 6 ? 'ประจำ' : 'Part-time' }))

const TREATMENT_TYPES = [
  { value: 'scale', label: 'ขูดหินปูน', search: 'scaling', count: 214 },
  { value: 'fill', label: 'อุดฟัน', search: 'filling', count: 160 },
  { value: 'ext', label: 'ถอนฟัน', search: 'extraction', count: 98 },
  { value: 'rct', label: 'รักษารากฟัน', search: 'root canal', count: 41 },
  { value: 'imp', label: 'รากฟันเทียม', search: 'implant', count: 12 },
  { value: 'ortho', label: 'จัดฟัน', search: 'orthodontics', count: 77 },
  { value: 'white', label: 'ฟอกสีฟัน', search: 'whitening', count: 23, disabled: true },
]

function DropdownSection() {
  const [status, setStatus] = useState('open')
  const [doctor, setDoctor] = useState('')
  const [branch, setBranch] = useState(8)
  const [types, setTypes] = useState(new Set(['scale', 'fill', 'ortho']))
  const [err, setErr] = useState('')
  const [drawer, setDrawer] = useState(false)
  return (
    <Section id="dropdowns" title="Dropdown — Select / SearchSelect"
      why="เมนูเลือกวาดในแอปเอง (ไม่ใช้เมนูของระบบปฏิบัติการ) หน้าตาเหมือนกันทุกเครื่อง และไม่ถูกตัดขอบเมื่ออยู่ใน Drawer หรือในตาราง"
      rules={[
        'Select = รายการสั้น (ไม่เกิน ~12 ตัวเลือก) เช่น สาขา สถานะ · พิมพ์ตัวอักษรเพื่อกระโดดไปตัวเลือกนั้นได้',
        'SearchSelect = รายการยาว เช่น แพทย์ พนักงาน ประเภทการรักษา · มีช่องค้นหาด้านบน เลือกได้หลายรายการ (multiple)',
        'แป้นพิมพ์: ↑ ↓ เลื่อน · Enter เลือก · Esc ปิดเมนู (ไม่ปิด Drawer ข้างหลัง) · ⌫ ลบรายการล่าสุด (แบบหลายรายการ)',
        'ข้อความบรรทัดที่สอง (hint) ใช้แยกคนชื่อซ้ำ เช่น "หมอเอ" สองคน · ตัวเลือกที่เลือกไม่ได้แสดงเป็นสีจาง',
      ]}>
      <Demo className="grid sm:grid-cols-2 gap-4">
        <Field label="สถานะ (Select)">
          <Select value={status} onChange={setStatus} options={[
            { value: 'all', label: 'ทั้งหมด' }, { value: 'open', label: 'ยังไม่เสร็จ' }, { value: 'done', label: 'เสร็จแล้ว' }, { value: 'cancel', label: 'ยกเลิก' },
          ]} />
        </Field>
        <Field label="สาขา (Select + ล้างได้)" hint="กด ✕ เพื่อกลับเป็น 'ทุกสาขา'"><Select value={branch} onChange={setBranch} options={BRANCH_OPTIONS} clearable placeholder="ทุกสาขา" /></Field>
        <Field label="แพทย์ (SearchSelect · มีกลุ่ม + hint)" required error={err || undefined}>
          <SearchSelect value={doctor} onChange={(v) => { setDoctor(v); setErr('') }} options={DOCTORS} placeholder="เลือกแพทย์…" clearable />
        </Field>
        <Field label="ประเภทการรักษา (SearchSelect · หลายรายการ)" hint='ลองค้น "implant" — ค้นได้แม้คำนั้นไม่แสดง'>
          <SearchSelect multiple values={types} onChange={setTypes} options={TREATMENT_TYPES} placeholder="ทุกประเภท" />
        </Field>
        <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => setErr(doctor ? '' : 'กรุณาเลือกแพทย์')}>ลองกดบันทึก (ตรวจช่องจำเป็น)</Button>
          <Button size="sm" variant="secondary" onClick={() => setDrawer(true)}>เปิดใน Drawer (เมนูไม่ถูกตัด)</Button>
          <span className="text-[12.5px] text-slate-500">ค่า: สถานะ={String(status)} · แพทย์={String(doctor || '—')} · ประเภท={[...types].join(', ') || '—'}</span>
        </div>
      </Demo>
      <Drawer open={drawer} onClose={() => setDrawer(false)} title="Dropdown ใน Drawer" subtitle="เมนูลอยเหนือ Drawer · Esc ครั้งแรกปิดเมนู ครั้งที่สองปิด Drawer" width="sm"
        footer={<DrawerFooter onCancel={() => setDrawer(false)} cancelLabel="ปิด" />}>
        <div className="rounded-2xl bg-white border border-slate-200 p-4 space-y-4">
          <Field label="แพทย์"><SearchSelect value={doctor} onChange={setDoctor} options={DOCTORS} placeholder="เลือกแพทย์…" /></Field>
          <div className="h-[52vh]" />
          <Field label="ด้านล่างจอ — เมนูจะเปิดขึ้นด้านบน"><Select value={status} onChange={setStatus} options={[{ value: 'all', label: 'ทั้งหมด' }, { value: 'open', label: 'ยังไม่เสร็จ' }, { value: 'done', label: 'เสร็จแล้ว' }]} /></Field>
        </div>
      </Drawer>
    </Section>
  )
}

const TODAY = bkkTodayISO()
const DEFAULT_FILTERS = { q: '', branch: '', status: 'all', range: { start: `${TODAY.slice(0, 8)}01`, end: TODAY } }

function FilterSection() {
  const { filters, set, reset, isDefault } = useFilters('kit-demo-2', DEFAULT_FILTERS, { daily: ['range'] })
  return (
    <Section id="filters" title="ค้นหา / กรอง — FilterCard + useFilters"
      why="ตัวกรองอยู่ในกล่องเดียวกันทุกหน้า ปุ่ม 'ล้างตัวกรอง' อยู่มุมขวาบนของกล่อง และกดได้เฉพาะเมื่อมีการกรอง"
      rules={['ระบบจำตัวกรองของแต่ละหน้าไว้ (ออกแล้วกลับมาก็ยังอยู่)', 'sticky = กล่องตัวกรองติดอยู่ใต้หัวหน้าเมื่อเลื่อนรายการยาว · compact = ไม่มีหัวกล่อง', 'ช่วงวันที่จะกลับเป็น "เดือนนี้" เมื่อขึ้นวันใหม่ — เปิดเช้าวันรุ่งขึ้นจะไม่ค้างที่ช่วงของเมื่อวาน', 'ลองเปลี่ยนค่าแล้วรีเฟรชหน้า — ค่าจะยังอยู่']}>
      <FilterCard onClear={reset} canClear={!isDefault} aside={<IconButton size="sm" icon={RefreshCw} label="รีเฟรช" />}>
        <Field label="ค้นหา" className="w-full sm:w-64"><SearchInput value={filters.q} onChange={(v) => set('q', v)} /></Field>
        <Field label="สาขา" className="w-44"><BranchSelect value={filters.branch} onChange={(v) => set('branch', v)} /></Field>
        <Field label="ช่วงวันที่" className="w-72"><DateRangePicker value={filters.range} onChange={(v) => set('range', v)} /></Field>
        <Field label="สถานะ"><Segmented value={filters.status} onChange={(v) => set('status', v)} options={[{ value: 'all', label: 'ทั้งหมด' }, { value: 'open', label: 'ค้าง', count: 12 }, { value: 'done', label: 'เสร็จ' }]} /></Field>
      </FilterCard>
      <FilterCard compact onClear={reset} canClear={!isDefault}>
        <Field label="ค้นหา (แบบ compact — ไม่มีหัวกล่อง ใช้ในส่วนย่อยของหน้า)" className="w-full sm:w-80"><SearchInput value={filters.q} onChange={(v) => set('q', v)} /></Field>
      </FilterCard>
      <p className="text-[12.5px] text-slate-500">ค่าปัจจุบัน: <code className="bg-slate-100 px-1 rounded">{JSON.stringify(filters)}</code></p>
    </Section>
  )
}

const FAKE_NAMES = [['สมชาย', 'ใจดี'], ['มณีรัตน์', 'สุขสันต์'], ['ก้องภพ', 'ทองดี'], ['ปวีณา', 'ศรีสุข'], ['ธนกร', 'มั่นคง']]
const BRANCHES = ['อารีย์', 'บางนา', 'ลาดพร้าว', 'รามอินทรา']
const STATUS = [['emerald', 'ชำระแล้ว'], ['amber', 'รอชำระ'], ['rose', 'ยกเลิก'], ['slate', 'ร่าง']]
const SAMPLE = Array.from({ length: 37 }, (_, i) => {
  const s = STATUS[(i * 7) % STATUS.length]
  return {
    id: 1000 + i,
    item: `รายการตัวอย่าง ${i + 1}`,
    branch: BRANCHES[i % BRANCHES.length],
    date: Date.UTC(2026, 9, 18 - (i % 28), 3 + (i % 9), (i * 13) % 60),
    amount: ((i * 7919) % 25000) + 300,
    tone: s[0],
    status: s[1],
    // Audit columns in the shapes Xano returns (epoch ms + employee-code addon).
    // Every 3rd row was edited later by someone else; the rest were never touched.
    created_at: Date.UTC(2026, 9, 18 - (i % 28), 2, 10 + (i % 40)),
    creator: { code: ['แนน', 'ราม', 'ฝน'][i % 3] },
    modified_at: i % 3 === 0 ? Date.UTC(2026, 9, 18 - (i % 28), 6, 45) : Date.UTC(2026, 9, 18 - (i % 28), 2, 10 + (i % 40)),
    modified_by: i % 3 === 0 ? 2 : null,
    modifier: i % 3 === 0 ? { code: 'ราม' } : null,
    // Invented names; every link points at Ram's own test record (1474).
    patient: { id: 1474, name: FAKE_NAMES[i % FAKE_NAMES.length][0], surname: FAKE_NAMES[i % FAKE_NAMES.length][1], HN: String(880000 + i * 37) },
  }
})

function DateRangeSection() {
  const [past, setPast] = useState({ start: `${TODAY.slice(0, 8)}01`, end: TODAY })
  const [capped, setCapped] = useState({ start: TODAY, end: TODAY })
  const [future, setFuture] = useState({ start: TODAY, end: TODAY })
  const [any, setAny] = useState({ start: '', end: '' })
  const [round, setRound] = useState(() => {
    const [start, end] = DATE_RANGE_PRESETS.thisRound.range(TODAY)
    return { start, end }
  })
  return (
    <Section id="daterange" title="ช่วงวันที่ — DateRangePicker"
      why="เลือกช่วงวันที่แบบจองตั๋วเครื่องบิน: คลิกวันเริ่มต้น แล้วคลิกวันสิ้นสุด ระหว่างเลือกแถบสีจะตามเมาส์ให้เห็นความยาวก่อนกด — ย้ายมาจากตัวเลือกในหน้าสถิติ (ตัวที่ดีที่สุดในระบบ) และปรับตามข้อตกลง"
      rules={[
        'แสดงเป็น dd/mm/yyyy ค.ศ. พร้อมจำนวนวัน · "วันนี้" คือวันของกรุงเทพ ไม่ใช่นาฬิกาเครื่อง',
        'หน้ารายงาน / ประวัติ: เลือกวันในอนาคตไม่ได้ · หน้านัดหมาย / ตารางเวร: allowFuture เลือกล่วงหน้าได้',
        'ปุ่มลัดด้านบนเลือกได้ในคลิกเดียว · คลิกวันก่อนวันเริ่มต้น = เริ่มเลือกใหม่ · Esc ปิดปฏิทินอย่างเดียว',
        'ข้อมูลโหลดครั้งเดียวต่อการเลือกหนึ่งครั้ง (หลังคลิกวันสิ้นสุด) · จอมือถือแสดงเดือนเดียว',
      ]}>
      <Demo className="grid sm:grid-cols-2 gap-4">
        <Field label="รายงาน (ค่าเริ่มต้น — ย้อนหลังเท่านั้น)"><DateRangePicker value={past} onChange={setPast} /></Field>
        <Field label="จำกัดไม่เกิน 60 วัน (maxDays)" hint="ปุ่มลัดที่ยาวเกินจะไม่แสดง"><DateRangePicker value={capped} onChange={setCapped} maxDays={60} /></Field>
        <Field label="นัดหมาย / ตารางเวร (allowFuture)"><DateRangePicker value={future} onChange={setFuture} allowFuture /></Field>
        <Field label="ไม่บังคับช่วง (clearable)" hint="ว่าง = ทุกวันที่"><DateRangePicker value={any} onChange={setAny} clearable /></Field>
        <Field label="ปุ่มลัดเลือกเอง (presets + clearable)" hint="ผลการโทรรากเทียม: 7 · 30 · 60 วัน · ทั้งหมด"><DateRangePicker value={any} onChange={setAny} presets={['last7', 'last30', 'last60']} clearable placeholder="ทั้งหมด" /></Field>
        <Field label="รอบเงินเดือน 26 → 25 (thisRound · lastRound)" hint="พนักงาน › ลงเวลา · ต้องใช้คู่กับ allowFuture เพราะรอบปัจจุบันจบในอนาคต"><DateRangePicker value={round} onChange={setRound} allowFuture maxDays={31} presets={['thisRound', 'lastRound', 'today', 'last7']} /></Field>
        <p className="sm:col-span-2 text-[12.5px] text-slate-500">ค่าที่ส่งให้ระบบ: <code className="bg-slate-100 px-1 rounded">{JSON.stringify(past)}</code> — วันที่ส่งเป็น YYYY-MM-DD เสมอ ไม่ใช่ข้อความที่แสดง</p>
      </Demo>
    </Section>
  )
}

function BranchSection() {
  const branches = useBranches()
  const [filter, setFilter] = useState(8)
  const [form, setForm] = useState('')
  const ids = branchFilterIds(filter)
  return (
    <Section id="branches" title="สาขา — BranchSelect + branches.js"
      why="สนามเป้าเป็นสำนักงาน ไม่มีคลินิก จึงไม่มีให้เลือกทั้งในฟอร์มและตัวกรอง แต่ข้อมูลเก่ายังมีสาขานี้อยู่ จึงนับรวมกับอารีย์"
      rules={[
        'ฟอร์ม (สร้าง/แก้ไขข้อมูล) → mode="form" : มีแต่สาขาที่เป็นคลินิกจริง',
        'ตัวกรอง → mode="filter" : มี "ทุกสาขา" ด้านบน · เลือกอารีย์ = ดึงข้อมูลของอารีย์ + สนามเป้า',
        'แสดงชื่อสาขาของข้อมูลเก่าที่เป็นสนามเป้า → "อารีย์"',
        'รายชื่อสาขาด้านล่างเป็นของจริงจากระบบ',
      ]}>
      <Demo className="grid sm:grid-cols-2 gap-4">
        <Field label="ตัวกรอง (mode=filter)" hint={ids ? `จะดึงข้อมูลสาขา id: ${ids.join(' + ')}` : 'จะดึงข้อมูลทุกสาขา'}>
          <BranchSelect value={filter} onChange={setFilter} />
        </Field>
        <Field label="ฟอร์ม (mode=form)" required><BranchSelect mode="form" value={form} onChange={setForm} /></Field>
        <div className="sm:col-span-2 text-[12.5px] text-slate-500">ข้อมูลเก่าที่บันทึกเป็นสาขา 1 แสดงเป็น: <b className="text-slate-700">{branchName(1, branches)}</b></div>
      </Demo>
    </Section>
  )
}

function TableSection() {
  const toast = useToast()
  const [mode, setMode] = useState('data')
  const [open, setOpen] = useState(null)
  const columns = useMemo(() => [
    { key: 'id', label: 'เลขที่', sortable: true, width: '90px', render: (r) => <span className="tabular-nums text-slate-500">#{r.id}</span> },
    { key: 'item', label: 'รายการ', sortable: true, render: (r) => <span className="font-medium text-slate-800">{r.item}</span> },
    { key: 'patient', label: 'คนไข้', sortable: true, sortValue: (r) => r.patient.name, render: (r) => <PatientName patient={r.patient} context="lab" /> },
    { key: 'branch', label: 'สาขา', sortable: true, hideBelow: 'md' },
    { key: 'date', label: 'วันที่', sortable: true, defaultDir: 'desc', render: (r) => <span className="tabular-nums">{fmtDate(r.date)}</span> },
    { key: 'amount', label: 'ยอดเงิน', sortable: true, defaultDir: 'desc', align: 'right', render: (r) => fmtMoney(r.amount) },
    { key: 'status', label: 'สถานะ', sortable: true, render: (r) => <Badge tone={r.tone}>{r.status}</Badge> },
  ], [])
  const [grouped, setGrouped] = useState(false)
  const t = useTableState(SAMPLE, columns, { pageSize: 10, sort: { key: 'date', dir: 'desc' } })
  const TONE_HEX = { emerald: '#10b981', amber: '#f59e0b', rose: '#f43f5e', slate: '#94a3b8' }
  const state = {
    data: {},
    loading: { loading: true, rows: [] },
    refreshing: { loading: true },
    empty: { rows: [] },
    nomatch: { rows: [], filtered: true, onClearFilters: () => setMode('data') },
    error: { error: 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ (HTTP 502)', onRetry: () => setMode('data') },
  }[mode]
  return (
    <Section id="table" title="ตาราง — DataTable + useTableState"
      why="ทุกรายการใช้ตารางเดียวกัน: หัวคอลัมน์คลิกเพื่อเรียงได้ แบ่งหน้าอยู่ด้านล่างเสมอ พร้อมจำนวนทั้งหมด และมีสถานะโหลด/ว่าง/ไม่พบ/ผิดพลาดในตัว"
      rules={[
        'คลิกแถว = เปิด Drawer แก้ไขรายการนั้น (ลองคลิก) · ใช้แป้น Tab + Enter ได้',
        'คลิกชื่อหรือ HN = เปิดหน้าคนไข้ในแท็บใหม่ (ไม่เปิด Drawer) · ตารางนี้สมมติเป็นตาราง Lab จึงเปิดที่แท็บ Lab',
        'ตัวเลขและเงินชิดขวาเสมอ · เรียงตามตัวอักษรไทยถูกต้อง · คอลัมน์ "สาขา" ซ่อนเมื่อจอแคบ',
        'รีเฟรชระหว่างที่มีข้อมูลแล้ว → ตารางจางลง ไม่กระพริบเป็นหน้าโหลด',
        'แถบสีซ้ายของแถว (rowAccent) ใช้แสดงสถานะ · หัวกลุ่ม (groupBy) ใช้แบ่งตามวัน/เดือน/ปี — ต้องเรียงข้อมูลตามกลุ่มก่อน',
      ]}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12.5px] text-slate-500">ลองดูสถานะ:</span>
        <Segmented size="sm" value={mode} onChange={setMode} options={[
          { value: 'data', label: 'ปกติ' }, { value: 'refreshing', label: 'กำลังรีเฟรช' }, { value: 'loading', label: 'โหลดครั้งแรก' },
          { value: 'empty', label: 'ว่าง' }, { value: 'nomatch', label: 'ไม่พบตามตัวกรอง' }, { value: 'error', label: 'ผิดพลาด' },
        ]} />
        <Switch size="sm" label="จัดกลุ่มตามวัน (groupBy)" checked={grouped} onChange={setGrouped} />
      </div>
      <DataTable columns={columns} {...t.tableProps} {...state} onRowClick={setOpen}
        rowAccent={(r) => TONE_HEX[r.tone]}
        groupBy={grouped ? (r) => fmtDate(r.date) : undefined}
        empty={{ title: 'ยังไม่มีรายการ', hint: 'รายการที่สร้างจะแสดงที่นี่', action: <Button size="sm" icon={Plus}>เพิ่มรายการแรก</Button> }} />
      <RecordDrawer row={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); toast.success('บันทึกแล้ว') }} />
    </Section>
  )
}

function RecordDrawer({ row, onClose, onSaved }) {
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  const [asking, setAsking] = useState(false)
  const toast = useToast()
  const current = draft ?? row
  const dirty = !!(draft && row && (draft.item !== row.item || String(draft.amount) !== String(row.amount)))
  const invalid = current && !(Number(current.amount) > 0)
  const close = () => { setDraft(null); onClose() }
  const save = () => { setSaving(true); setTimeout(() => { setSaving(false); setDraft(null); onSaved() }, 1200) }
  if (!row) return null
  return (
    <>
      <Drawer open title={row.item} subtitle={`#${row.id} · ${row.branch} · ${fmtDateTime(row.date)}`} icon={FileText}
        onClose={close} busy={saving} dirty={dirty} record={row} recordTable="ตัวอย่าง"
        headerActions={<Badge tone={row.tone}>{row.status}</Badge>}
        footer={<DrawerFooter onCancel={close} onSave={save} saving={saving} canSave={dirty && !invalid}
          status={invalid ? 'ยอดเงินต้องมากกว่า 0' : dirty ? 'มีการแก้ไขที่ยังไม่บันทึก' : 'ยังไม่มีการแก้ไข'} statusTone={invalid ? 'error' : dirty ? 'warn' : 'muted'}
          danger={<Button variant="danger-soft" size="sm" icon={Trash2} onClick={() => setAsking(true)}>ลบ</Button>}
          secondary={dirty && <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>คืนค่าเดิม</Button>} />}>
        <div className="rounded-2xl bg-white border border-slate-200 p-4 space-y-4">
          <Field label="รายการ" required><Input value={current.item} onChange={(e) => setDraft({ ...current, item: e.target.value })} /></Field>
          <Field label="ยอดเงิน (บาท)" required error={invalid ? 'ต้องมากกว่า 0' : undefined}><Input inputMode="numeric" value={current.amount} onChange={(e) => setDraft({ ...current, amount: e.target.value })} /></Field>
          <p className="text-[12.5px] text-slate-500">ลองแก้ข้อความแล้วกด Esc หรือคลิกนอกกล่อง — ระบบจะถามก่อนทิ้งการแก้ไข · ระหว่างกำลังบันทึกจะปิดไม่ได้</p>
          <p className="text-[12.5px] text-slate-500">มุมล่างซ้าย: เลขที่ · สร้างโดยใคร เมื่อไร · แก้ไขล่าสุดโดยใคร เมื่อไร (แถวที่ 1, 4, 7 … มีการแก้ไข · แถวอื่นไม่เคยแก้จึงไม่แสดง "แก้ไข")</p>
        </div>
      </Drawer>
      {asking && (
        <ConfirmDialog tone="danger" title="ลบรายการนี้?" message={`${row.item} (#${row.id}) จะถูกลบ — ย้อนกลับไม่ได้`} confirmLabel="ลบรายการ"
          onCancel={() => setAsking(false)} onConfirm={() => { setAsking(false); close(); toast.success('ลบแล้ว', { action: { label: 'เลิกทำ', onClick: () => toast.info('กู้คืนแล้ว (ตัวอย่าง)') } }) }} />
      )}
    </>
  )
}

function TabsSection() {
  const [tab, setTab] = useState('list')
  const [sub, setSub] = useState('pending')
  const [view, setView] = useState('table')
  const [metric, setMetric] = useState('total')
  return (
    <Section id="tabs" title="แท็บ & ตัวเลือก — Tabs / SubTabs / Segmented / ViewToggle"
      why="แท็บมี 2 ระดับเท่านั้น ระดับที่ 3 ให้ใช้ตัวเลือก (Segmented) แทน แท็บที่ผู้ใช้ไม่มีสิทธิ์จะไม่แสดง (ไม่แสดงแบบกดไม่ได้)"
      rules={['Tabs (เส้นใต้) = ส่วนหลักของหน้า · SubTabs (ป้ายเล็ก) = มุมมองย่อยในส่วนนั้น', 'ถ้าแท็บเป็นลิงก์ (to) ปุ่ม Back ของเบราว์เซอร์จะใช้ได้และส่งลิงก์ให้คนอื่นได้', 'ตัวเลขแดง = มีงานรอผู้ใช้คนนี้ · ตัวเลขเทา = จำนวนเฉย ๆ']}>
      <Demo label="ระดับ 1 + ระดับ 2">
        <Tabs value={tab} onChange={setTab} items={[{ key: 'list', label: 'รายชื่อ' }, { key: 'leave', label: 'การลา', badge: 3, badgeTone: 'alert' }, { key: 'docs', label: 'เอกสาร', badge: 12 }]} />
        <SubTabs className="mt-3" value={sub} onChange={setSub} items={[{ key: 'pending', label: 'รอพิจารณา', badge: 3, badgeTone: 'alert' }, { key: 'approved', label: 'อนุมัติแล้ว' }, { key: 'all', label: 'ทั้งหมด' }]} />
      </Demo>
      <Demo label="Segmented + ViewToggle" className="flex flex-wrap items-center gap-3">
        <Segmented value={metric} onChange={setMetric} options={[{ value: 'total', label: 'รายได้รวม' }, { value: 'clinic', label: 'ส่วนคลินิก' }, { value: 'doctor', label: 'ส่วนแพทย์' }]} />
        <ViewToggle value={view} onChange={setView} views={['table', 'card', 'calendar']} />
      </Demo>
    </Section>
  )
}

function ChoiceSection() {
  const [walkIn, setWalkIn] = useState(true)
  const [all, setAll] = useState(false)
  const [autoFee, setAutoFee] = useState(true)
  const [tagType, setTagType] = useState('Adult')
  const [symptoms, setSymptoms] = useState(new Set(['ไข้']))
  return (
    <Section id="choices" title="Checkbox / Switch / ChoiceChips"
      why="ใช่/ไม่ใช่ และการเลือกหลายอย่าง — เดิมแต่ละหน้าใช้ checkbox ของเบราว์เซอร์ หรือทำปุ่มสีเอง"
      rules={['Checkbox = ใช่/ไม่ใช่ในฟอร์ม (บันทึกเมื่อกดบันทึก) · Switch = เปิด/ปิดการตั้งค่าที่มีผลทันที', 'ChoiceChips = เลือกจากรายการสั้นที่แต่ละตัวมีสีของตัวเอง (ประเภทฟัน, ประเภทใบยินยอม, อาการ) · ไม่มีสี → ใช้ Segmented']}>
      <Demo className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-3">
          <Checkbox label="เคสแทรก (walk-in)" hint="ไม่ได้นัดล่วงหน้า" checked={walkIn} onChange={setWalkIn} />
          <Checkbox label="เลือกทั้งหมด" indeterminate={!all} checked={all} onChange={setAll} />
          <Checkbox label="ปิดใช้งาน" disabled />
          <Switch label="คิดค่าธรรมเนียมอัตโนมัติ" hint="มีผลทันที" checked={autoFee} onChange={setAutoFee} />
        </div>
        <div className="space-y-3">
          <Field label="ประเภท (เลือกหนึ่ง สีต่อประเภท)">
            <ChoiceChips value={tagType} onChange={setTagType} options={[
              { value: 'Adult', label: 'Adult', color: '#2563eb' }, { value: 'Ped', label: 'Ped', color: '#db2777' },
              { value: 'Ortho', label: 'Ortho', color: '#7c3aed' }, { value: 'Full Mouth', label: 'Full Mouth', color: '#0d9488' },
            ]} />
          </Field>
          <Field label="อาการ (เลือกได้หลายอย่าง)">
            <ChoiceChips multiple values={symptoms} onChange={setSymptoms} options={['ไข้', 'ไอ', 'เจ็บคอ', 'น้ำมูก'].map((v) => ({ value: v, label: v, color: '#e11d48' }))} />
          </Field>
        </div>
      </Demo>
    </Section>
  )
}

function OverlaySection() {
  const toast = useToast()
  const [drawer, setDrawer] = useState(false)
  const [modal, setModal] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [busyAsk, setBusyAsk] = useState(false)
  const [busy, setBusy] = useState(false)
  return (
    <Section id="overlays" title="Drawer / Modal / ConfirmDialog"
      why="เดิมมีกล่องลอย 77 แบบ เขียนแยกกัน 41 แบบกด Esc ไม่ได้ และส่วนใหญ่คลิกพลาดนอกกล่องแล้วข้อมูลที่พิมพ์หายทันที — ตอนนี้กฎอยู่ในตัวกล่องเอง"
      rules={[
        'Drawer (ด้านขวา) = ดู/แก้ไขข้อมูลหนึ่งรายการ · Modal (กลางจอ) = คำถามหรือฟอร์มสั้น ๆ · ConfirmDialog = ใช่/ไม่ใช่',
        'Esc / คลิกนอกกล่อง / ✕ = ปิด · ระหว่างบันทึกปิดไม่ได้ · มีการแก้ไขค้าง → ถามก่อน "ทิ้งการแก้ไข?"',
        'ท้าย Drawer: [ลบ] … [เหตุผลที่บันทึกไม่ได้] … [ยกเลิก] [บันทึก] — ปุ่มยกเลิกใช้คำว่า "ยกเลิก" เสมอ',
        'ข้อมูลที่มีอยู่แล้ว: มุมล่างซ้ายแสดง #ID · สร้าง (ใคร เมื่อไร) · แก้ไข (ใคร เมื่อไร) — ฟอร์มสร้างใหม่ไม่แสดง',
        'กล่องยืนยันการลบต้องบอกว่าอะไรจะหาย ไม่ใช่แค่ "แน่ใจไหม"',
      ]}>
      <Demo className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => setDrawer(true)}>เปิด Drawer</Button>
        <Button variant="secondary" onClick={() => setModal(true)}>เปิด Modal</Button>
        <Button variant="danger-soft" icon={Trash2} onClick={() => setConfirm(true)}>ลบ (ยืนยันก่อน)</Button>
        <Button variant="danger-soft" icon={Trash2} onClick={() => setBusyAsk(true)}>ลบ (ยืนยัน + กำลังลบ)</Button>
      </Demo>
      {busyAsk && <ConfirmDialog tone="danger" busy={busy} title="ลบรายการตัวอย่าง?" message="ระหว่างกำลังลบ ปุ่มทั้งสองกดไม่ได้ และกด Esc / คลิกนอกกล่องไม่ปิด — กดซ้ำส่งสองครั้งไม่ได้" confirmLabel={busy ? 'กำลังลบ…' : 'ลบรายการ'}
        onCancel={() => setBusyAsk(false)} onConfirm={() => { setBusy(true); setTimeout(() => { setBusy(false); setBusyAsk(false); toast.success('ลบแล้ว') }, 1500) }} />}
      <Drawer open={drawer} onClose={() => setDrawer(false)} title="ดูข้อมูลอย่างเดียว" subtitle="ไม่มีการแก้ไข → ไม่มีปุ่มบันทึก" width="sm"
        footer={<DrawerFooter onCancel={() => setDrawer(false)} cancelLabel="ปิด" />}>
        <div className="rounded-2xl bg-white border border-slate-200 p-4 text-[13.5px] text-slate-600">มุมมองอ่านอย่างเดียว ปุ่มท้ายเป็น "ปิด" ได้ เพราะไม่มีอะไรให้ยกเลิก</div>
      </Drawer>
      <Modal open={modal} onClose={() => setModal(false)} icon={Download} title="ส่งออกรายการ" subtitle="ไฟล์ CSV เปิดใน Excel ได้ (ภาษาไทยไม่เพี้ยน)" width="sm"
        footer={<DrawerFooter onCancel={() => setModal(false)} onSave={() => { setModal(false); toast.success('ส่งออกแล้ว', { detail: 'รายการ_2026-10-18.csv' }) }} saveLabel="ส่งออก" />}>
        <ExportRange />
      </Modal>
      {confirm && <ConfirmDialog tone="danger" title="ลบรายการตัวอย่าง?" message="รายการตัวอย่าง 1 (#1000) และไฟล์แนบ 2 ไฟล์จะถูกลบ — ย้อนกลับไม่ได้" confirmLabel="ลบรายการ"
        onCancel={() => setConfirm(false)} onConfirm={() => { setConfirm(false); toast.success('ลบแล้ว') }} />}
    </Section>
  )
}

function ExportRange() {
  const [range, setRange] = useState('this')
  return <Field label="ช่วงเวลา"><Select value={range} onChange={setRange} options={[{ value: 'this', label: 'เดือนนี้' }, { value: 'last', label: 'เดือนที่แล้ว' }, { value: 'q', label: '3 เดือนล่าสุด' }]} /></Field>
}

function ToastSection() {
  const toast = useToast()
  return (
    <Section id="toast" title="ข้อความแจ้ง — Toast"
      why="แจ้งผลหลังทำรายการด้วยกล่องเล็กด้านล่างกลางจอ หายเอง — แทนที่การปิดเงียบ ๆ แบนเนอร์หลายสี หรือ toast ที่ไม่ยอมหาย"
      rules={['สำเร็จ = หายใน 3 วินาที · ผิดพลาด = 6 วินาที (และถ้าต้องแก้ข้อมูล ให้แสดงข้อความแดงที่ฟอร์มด้วย)', 'การกระทำที่ย้อนได้ ให้มีปุ่ม "เลิกทำ" ใน toast แทนการถามยืนยัน']}>
      <Demo className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => toast.success('บันทึกแล้ว')}>สำเร็จ</Button>
        <Button variant="secondary" onClick={() => toast.error('บันทึกไม่สำเร็จ', { detail: 'HTTP 502 — ลองอีกครั้ง' })}>ผิดพลาด</Button>
        <Button variant="secondary" onClick={() => toast.info('มีการอัปเดตจากเครื่องอื่น')}>ข้อมูล</Button>
        <Button variant="secondary" onClick={() => toast.success('ย้ายไป "เสร็จแล้ว" 3 รายการ', { action: { label: 'เลิกทำ', onClick: () => toast.info('ย้อนกลับแล้ว') } })}>พร้อมปุ่มเลิกทำ</Button>
      </Demo>
    </Section>
  )
}

function StatesSection() {
  return (
    <Section id="states" title="สถานะ — Loading / EmptyState / NoMatchState / ErrorState"
      why="แต่ละพื้นที่อยู่ในสถานะเดียวเท่านั้น: กำลังโหลด · ผิดพลาด · ว่าง · ไม่พบตามตัวกรอง · มีข้อมูล — เดิมมีห้าหน้าที่แสดง 'ผิดพลาด' กับ 'ไม่มีข้อมูล' พร้อมกัน"
      rules={['"ว่าง" (ยังไม่มีอะไร) กับ "ไม่พบตามตัวกรอง" (มีแต่ถูกกรองออก) ต้องต่างกัน และอย่างหลังต้องมีปุ่มล้างตัวกรอง', 'โหลดไม่สำเร็จต้องไม่ดูเหมือนรายการว่าง — และมีปุ่มลองอีกครั้ง', 'Banner = ข้อความที่อยู่จนกว่าเหตุจะหมด (แก้จากเครื่องอื่น, ปิดยอดแล้ว, แสดงไม่ครบ) · ผลการบันทึกใช้ Toast']}>
      <div className="grid md:grid-cols-2 gap-4">
        <Demo label="Loading"><Loading /><div className="mt-2"><Loading variant="inline" label="กำลังคำนวณ…" /></div></Demo>
        <Demo label="ErrorState + InlineError"><ErrorState message="เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ (HTTP 502)" onRetry={() => {}} /><div className="mt-3"><InlineError>เลือกสาขาก่อนบันทึก</InlineError></div></Demo>
        <Demo label="EmptyState — card"><EmptyState icon={Inbox} title="ยังไม่มีงาน Lab" hint="งานที่สร้างจากการรักษาจะแสดงที่นี่" action={<Button size="sm" icon={Plus}>เพิ่มงาน Lab</Button>} /></Demo>
        <Demo label="NoMatchState"><NoMatchState variant="card" onClear={() => {}} /></Demo>
        <Demo label="EmptyState — compact (ในการ์ดเล็ก)"><EmptyState variant="compact" title="ยังไม่มีโน้ต" action={<Button size="sm" variant="soft" icon={Plus}>เพิ่มโน้ต</Button>} /></Demo>
      </div>
      <Demo label="Banner — ข้อความที่ค้างอยู่จนกว่าจะหมดเหตุ (ไม่ใช่ผลการบันทึก ไม่ใช่โหลดไม่สำเร็จ)" className="space-y-2">
        <Banner tone="warn" title="มีการแก้ไข chart นี้จากเครื่องอื่น" action={<Button size="sm" variant="secondary">โหลดใหม่</Button>}>ข้อมูลบนหน้านี้อาจไม่ใช่ล่าสุด</Banner>
        <Banner tone="info" icon={CalendarClock}>วันนี้ปิดยอดแล้ว — แก้ไขรายการของวันนี้ไม่ได้</Banner>
        <Banner tone="error" title="ลบแท็กไม่สำเร็จ">HTTP 502 — ลองอีกครั้ง</Banner>
        <Banner tone="success" onClose={() => {}}>เชื่อมต่อ LINE แล้ว</Banner>
      </Demo>
    </Section>
  )
}

function BadgeSection() {
  return (
    <Section id="badges" title="ป้ายสถานะ — Badge / CountBadge"
      why="ทรงเดียว (มุมมนเต็ม) สีมีความหมายเดียวกันทั้งระบบ"
      rules={['เทา = ปิด/ยังไม่เริ่ม · น้ำเงิน = กำลังดำเนินการ · เขียว = เสร็จ/อนุมัติ/ชำระแล้ว · เหลือง = รอใครสักคน · แดง = มีปัญหา/ปฏิเสธ/เลยกำหนด · ม่วง = หมวดพิเศษ (ใช้น้อย)', 'สีจากข้อมูล (วิธีชำระเงิน, ประเภทนัด) ใช้ color="#..." · ตัวเลขที่โหลดไม่สำเร็จแสดง — ไม่ใช่ 0']}>
      <Demo className="flex flex-wrap items-center gap-2">
        <Badge>ร่าง</Badge><Badge tone="brand" dot>กำลังทำ</Badge><Badge tone="emerald" dot>อนุมัติแล้ว</Badge>
        <Badge tone="amber" dot>รอพิจารณา</Badge><Badge tone="rose" dot>เลยกำหนด</Badge><Badge tone="violet">โปรโมชั่น</Badge>
        <span className="mx-2 text-slate-300">|</span><CountBadge n={3} tone="alert" /><CountBadge n={42} /><CountBadge n={250} tone="alert" /><CountBadge unknown />
        <span className="mx-2 text-slate-300">|</span><Badge color="#0d9488" dot>เงินสด</Badge><Badge color="#7c3aed" dot>โอน</Badge><Badge color="#db2777">บัตรเครดิต</Badge>
      </Demo>
    </Section>
  )
}

function StatSection() {
  const [pick, setPick] = useState('')
  return (
    <Section id="stats" title="ตัวเลขสรุป — StatTile"
      why="แถวตัวเลขบนการ์ดรายงาน — ป้ายบน ตัวเลขใหญ่ บรรทัดย่อยหนึ่งบรรทัด"
      rules={['เขียว (good) เฉพาะผลที่สำคัญที่สุด เช่น นัดได้ จ่ายแล้ว · เหลือง (warn) สิ่งที่ต้องตามต่อ', 'กดได้เมื่อใช้เป็นตัวกรอง (onClick + active) · โหลดไม่สำเร็จแสดง — ไม่ใช่ 0', 'วางเป็น grid เพื่อให้ขอบเท่ากันทุกแถว']}>
      <Demo className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="ผ่านรีวิวแล้ว" value="208" />
        <StatTile label="โทรแล้ว ยังไม่นัด" value="14" tone="warn" sub="⚠ จองแล้วแต่ไม่พบนัด 2" onClick={() => setPick(pick === 'c' ? '' : 'c')} active={pick === 'c'} />
        <StatTile label="นัดได้" value="4" tone="good" sub="22% ของคนที่โทรแล้ว" onClick={() => setPick(pick === 's' ? '' : 's')} active={pick === 's'} />
        <StatTile label="จ่ายแล้ว" value="—" sub="โหลดไม่สำเร็จ" />
      </Demo>
    </Section>
  )
}

function FormatSection() {
  const samples = [
    ['epoch ms จาก Xano (created_at)', Date.UTC(2026, 7, 7, 18, 30)],
    ['วันที่อย่างเดียว (date column)', '2026-08-08'],
    ['ไม่มีค่า', null],
  ]
  return (
    <Section id="format" title="วันที่ & เงิน — format.js"
      why="แทนฟังก์ชันวันที่ ~71 ตัวและรายชื่อเดือน 38 ชุด — อ่านเวลาแบบกรุงเทพเสมอ ไม่ว่าเครื่องจะตั้งเวลาอะไร ค่าว่างแสดง '—' ไม่ใช่ปี 1970"
      rules={['หน้าจอ = ค.ศ. dd/mm/yyyy (ตามค่าตัดสินใจ #1) · เอกสารที่พิมพ์ = พ.ศ. เต็ม (fmtDocDate) · บรรทัด log ที่ footer ของเอกสาร = ค.ศ. ภาษาอังกฤษ (docFooterLine)', 'เงิน = จำนวนเต็ม คั่นหลักพัน ไม่มีสัญลักษณ์ ชิดขวา · หน้าบัญชีใช้ทศนิยม 2 ตำแหน่ง · ไม่ทราบค่า = "—" ไม่ใช่ 0']}>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13px] min-w-[760px]">
          <thead><tr className="bg-slate-50/60 border-b border-slate-200 text-[12px] text-slate-500 text-left">
            <th className="px-4 py-2.5">ข้อมูลเข้า</th><th className="px-4 py-2.5">fmtDate</th><th className="px-4 py-2.5">fmtDateTime</th>
            <th className="px-4 py-2.5">fmtDateText (weekday)</th><th className="px-4 py-2.5">fmtMonth</th><th className="px-4 py-2.5">fmtDocDate (เอกสาร)</th>
          </tr></thead>
          <tbody>{samples.map(([label, v]) => (
            <tr key={label} className="border-b border-slate-100 last:border-0 tabular-nums">
              <td className="px-4 py-2.5 text-slate-500">{label}</td><td className="px-4 py-2.5">{fmtDate(v)}</td><td className="px-4 py-2.5">{fmtDateTime(v)}</td>
              <td className="px-4 py-2.5">{fmtDateText(v, { weekday: true })}</td><td className="px-4 py-2.5">{fmtMonth(v)}</td><td className="px-4 py-2.5">{fmtDocDate(v)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead><tr className="bg-slate-50/60 border-b border-slate-200 text-[12px] text-slate-500 text-left">
            <th className="px-4 py-2.5">fmtMoney</th><th className="px-4 py-2.5 text-right">ผลลัพธ์</th>
          </tr></thead>
          <tbody>{[
            ['12345.6 (ค่าเริ่มต้น)', fmtMoney(12345.6)], ['12345.6, decimals: 2', fmtMoney(12345.6, { decimals: 2 })],
            ['427.5, maxDecimals: 2 (ค่า DF)', fmtMoney(427.5, { maxDecimals: 2 })], ['427, maxDecimals: 2', fmtMoney(427, { maxDecimals: 2 })],
            ["12345, currency: 'prefix'", fmtMoney(12345, { currency: 'prefix' })], ["12345, currency: 'suffix'", fmtMoney(12345, { currency: 'suffix' })],
            ['docFooterLine({ printedAt: now, treatmentIds: 1234 })', docFooterLine({ printedAt: Date.now(), treatmentIds: '1234' })],
            ['-500', fmtMoney(-500)], ["0, zero: '—'", fmtMoney(0, { zero: '—' })], ['null (ไม่ทราบ)', fmtMoney(null)], ['fmtBytes(1536000)', fmtBytes(1536000)],
          ].map(([a, b]) => (
            <tr key={a} className="border-b border-slate-100 last:border-0"><td className="px-4 py-2 text-slate-500"><code>{a}</code></td><td className="px-4 py-2 text-right tabular-nums">{b}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </Section>
  )
}

function LinkSection() {
  const sample = { id: 1474, name: 'ตัวอย่าง', surname: 'ทดสอบ', HN: '000000' }
  const ctx = [['default', 'ทั่วไป (ค่าเริ่มต้น)'], ['appointment', 'จากหน้านัดหมาย'], ['lab', 'จากตาราง Lab'], ['chart', 'จาก chart'], ['followup', 'จากงานติดตาม']]
  return (
    <Section id="links" title="ลิงก์คนไข้ — PatientName / PatientLink"
      why="เปิดคนไข้จากชื่อ นามสกุล หรือ HN เท่านั้น (ไม่ใช่ทั้งแถว) เป็นลิงก์จริง เปิดแท็บใหม่ รายการเดิมยังอยู่ ⌘-คลิก / คลิกกลาง / คัดลอกลิงก์ได้"
      rules={['เปิดที่แท็บการรักษาเป็นหลัก · หน้าที่เกี่ยวกับนัดหมายเปิดแท็บนัดหมาย · ตาราง Lab เปิดแท็บ Lab — กำหนดด้วย context ไม่ต้องจำ path', 'ไม่มีคนไข้ (id 0) → แสดงเป็นข้อความธรรมดา ไม่เป็นลิงก์']}>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13.5px] min-w-[640px]">
          <thead><tr className="bg-slate-50/60 border-b border-slate-200 text-[12px] text-slate-500 text-left">
            <th className="px-4 py-2.5">context</th><th className="px-4 py-2.5">ใช้เมื่อ</th><th className="px-4 py-2.5">แสดงผล</th><th className="px-4 py-2.5">เปิดที่</th>
          </tr></thead>
          <tbody>{ctx.map(([c, label]) => (
            <tr key={c} className="border-b border-slate-100 last:border-0">
              <td className="px-4 py-2.5"><code className="text-[12.5px]">{c}</code></td><td className="px-4 py-2.5 text-slate-600">{label}</td>
              <td className="px-4 py-2.5"><PatientName patient={sample} context={c} /></td>
              <td className="px-4 py-2.5 text-[12.5px] text-slate-500"><code>{patientHref(1474, { context: c })}</code></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <Demo label="แบบบรรทัดเดียว · ไม่มีคนไข้" className="flex flex-wrap items-center gap-6 text-[13.5px]">
        <PatientName patient={sample} inline />
        <PatientName patient={{ id: 0, name: 'ไม่ได้ผูกคนไข้' }} inline />
      </Demo>
      <Demo label="ชื่อเล่น · badge · meta — รวมคอลัมน์เล็ก ๆ (อายุ สาขา) ไว้ใต้ชื่อ แทนการเพิ่มคอลัมน์" className="text-[13.5px]">
        <PatientName patient={sample} nickname="ฝน" badge={<Badge tone="amber">Warm Case</Badge>} meta="34 ปี · อารีย์" />
        <PatientName patient={sample} avatar meta="34 ปี · อารีย์" />
      </Demo>
    </Section>
  )
}
